import { Link } from "@/components/site/LocaleLink";
import { Stethoscope, Snowflake, ShieldCheck, Truck, Target, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Picture } from "@/components/site/Picture";
import { Partners } from "@/components/home/Partners";
import { SupportCTA } from "@/components/home/SupportCTA";
import { Marquee } from "@/components/site/Marquee";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useSeo } from "@/hooks/useSeo";
import { IMG } from "@/data/images";

const VALUE_ICONS = [ShieldCheck, Snowflake, Stethoscope, Truck];
const TEAM_ICONS = [Stethoscope, Snowflake, Truck];

export default function AboutPage() {
  const { t, isRtl } = useLang();
  useSeo({
    title: isRtl ? "من نحن | شركة الأبرد" : "About Us | Al-Abrad",
    description: t.aboutPage.sub,
  });

  const teams = [
    { t: t.aboutPage.values[2].t, d: t.supportPage.services[0].d },
    { t: t.aboutPage.values[1].t, d: t.why.items[0].d },
    { t: t.aboutPage.values[3].t, d: t.why.items[3].d },
  ];

  const marqueeItems = isRtl
    ? ["أصالة", "سلسلة تبريد", "خبرة عملية", "موثوقية", "تغطية وطنية"]
    : ["Authenticity", "Cold Chain", "Practical Expertise", "Reliability", "Nationwide"];

  return (
    <>
      <PageHero title={t.aboutPage.title} subtitle={t.aboutPage.sub} image={IMG.founder} crumb={t.nav.about} />

      {/* mission */}
      <section className="bg-cream py-24 lg:py-32">
        <div className="container-x grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal className="relative overflow-hidden rounded-2xl border border-line">
            <Picture
              img={IMG.healthyFlock}
              alt="Healthy broiler flock"
              className="aspect-[5/4] w-full object-cover"
              sizes="(min-width: 1024px) 50vw, 100vw"
            />
          </Reveal>
          <div>
            <Reveal>
              <p className="eyebrow mb-4 flex items-center gap-2 text-gold-ink">
                <Target className="h-4 w-4" /> {t.aboutPage.missionK}
              </p>
            </Reveal>
            <Reveal i={1}>
              <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-forest sm:text-4xl">
                {t.about.title}
              </h2>
            </Reveal>
            <Reveal i={2}>
              <p className="mt-6 text-base leading-relaxed text-stone sm:text-lg">{t.aboutPage.mission}</p>
            </Reveal>
            <Reveal i={3}>
              <p className="mt-4 text-base leading-relaxed text-stone sm:text-lg">{t.about.p2}</p>
            </Reveal>
            <Reveal i={4}>
              <blockquote className="mt-8 border-s-4 border-gold ps-6 font-display text-xl font-semibold italic leading-snug text-forest">
                “{t.about.quote}”
                <footer className="mt-3 text-sm not-italic text-stone">— {t.about.quoteBy}</footer>
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee items={marqueeItems} />

      {/* values */}
      <section className="bg-white py-24 lg:py-32">
        <div className="container-x">
          <Reveal>
            <h2 className="mb-14 max-w-2xl font-display text-4xl font-extrabold leading-tight tracking-tight text-forest sm:text-5xl">
              {t.aboutPage.valuesTitle}
            </h2>
          </Reveal>
          <RevealGroup className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {t.aboutPage.values.map((v, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <RevealItem
                  key={i}
                  data-testid={`value-${i}`}
                  className="group rounded-2xl border border-line bg-cream p-8 transition-all duration-500 hover:-translate-y-1 hover:border-forest/30"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-forest text-gold-light transition-transform duration-500 group-hover:rotate-6">
                    <Icon className="h-7 w-7" strokeWidth={1.6} />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-bold text-forest">{v.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone">{v.d}</p>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* team */}
      <section className="bg-cream py-24 lg:py-28">
        <div className="container-x">
          <Reveal>
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-forest sm:text-5xl">
              {t.aboutPage.teamTitle}
            </h2>
          </Reveal>
          <Reveal i={1}>
            <p className="mt-4 max-w-xl text-base text-stone sm:text-lg">{t.aboutPage.teamSub}</p>
          </Reveal>
          <RevealGroup className="mt-12 grid gap-6 sm:grid-cols-3">
            {teams.map((tm, i) => {
              const Icon = TEAM_ICONS[i];
              return (
                <RevealItem
                  key={i}
                  className="rounded-2xl border border-line bg-white p-8"
                  data-testid={`team-${i}`}
                >
                  <Icon className="h-8 w-8 text-gold" strokeWidth={1.6} />
                  <h3 className="mt-5 font-display text-lg font-bold text-forest">{tm.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone">{tm.d}</p>
                </RevealItem>
              );
            })}
          </RevealGroup>
          <Reveal i={2}>
            <Link
              to="/support"
              className="group mt-12 inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-dark"
            >
              {t.common.getSupport}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl-flip" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Partners />
      <SupportCTA />
    </>
  );
}
